import { useState, useEffect } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import theme from "../theme";
import { probeInternet } from "../lib/connectivity";
import { supabase } from "../supabaseClient";

const appVersion = () => (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev");

// Tripwire: if the banner comes back within 10 minutes of a Reload tap, say so
// in seapa_debug_log (step update_banner_back) with the versions and the
// worker states, so the cause is read from the next real case instead of
// guessed. Two builds served in turn never reproduced it in Chromium or
// WebKit (2026-10-12). sessionStorage, not localStorage: the marker belongs
// to the tab that tapped (it survives that tab's reload, other tabs never see
// it).
const RELOAD_TAP_KEY = "updateReloadTap";
function rememberReloadTap() {
  try { sessionStorage.setItem(RELOAD_TAP_KEY, JSON.stringify({ at: Date.now(), from: appVersion() })); } catch { /* ignore */ }
}
// The marker is cleared only once the row is written: on boat or PPU wifi
// the insert fails quietly (supabase-js resolves with { error }), and the
// next time the banner shows inside the window it tries again.
async function reportIfBannerCameBack() {
  let tap = null;
  try { tap = JSON.parse(sessionStorage.getItem(RELOAD_TAP_KEY) || "null"); } catch { /* ignore */ }
  if (!tap?.at) return;
  // Clear only the tap this report is about: a newer tap made while the
  // insert was in flight keeps its own marker (Codex on #582).
  const clear = () => {
    try {
      const now = JSON.parse(sessionStorage.getItem(RELOAD_TAP_KEY) || "null");
      if (now?.at === tap.at) sessionStorage.removeItem(RELOAD_TAP_KEY);
    } catch { /* ignore */ }
  };
  if (Date.now() - tap.at > 10 * 60 * 1000) return clear();
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const msg = [
      `tapped on v=${tap.from}`, `now v=${appVersion()}`, `${Math.round((Date.now() - tap.at) / 1000)}s after the tap`,
      `controlled=${!!navigator.serviceWorker?.controller}`, `active=${reg?.active?.state || "none"}`,
      `waiting=${reg?.waiting?.state || "none"}`, `installing=${reg?.installing?.state || "none"}`,
      navigator.userAgent,
    ].join(" | ").slice(0, 900);
    const { error } = await supabase.from("seapa_debug_log").insert({ voyage_number: null, step: "update_banner_back", msg });
    if (!error) clear();
  } catch { /* the log is a bonus */ }
}

// The worker a Reload tap is waiting on: the waiting one, or one another tab
// already told to take over (it has left the waiting slot and is activating).
function updatingWorker(reg) {
  return reg?.waiting || (reg?.active?.state === "activating" ? reg.active : null);
}

// Resolves once no new worker is left waiting or activating, or after ms.
// Reloading before then can land the page back on the OLD copy with the new
// one still waiting, so the banner comes straight back (suspected cause of
// Jill's three banners in a row on one tab, 2026-10-12; not reproduced). If
// the worker we wait on is replaced by a newer deploy (it turns redundant),
// tell the replacement to take over and wait on that one (Codex on #582).
async function untilActivated(reg, ms) {
  const deadline = Date.now() + ms;
  for (let worker = updatingWorker(reg); worker && Date.now() < deadline; worker = updatingWorker(reg)) {
    try { worker.postMessage({ type: "SKIP_WAITING" }); } catch { /* ignore */ }
    await new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        worker.removeEventListener("statechange", check);
        resolve();
      };
      const check = () => {
        if (worker.state === "activated" || worker.state === "redundant") done();
      };
      const timer = setTimeout(done, Math.max(0, deadline - Date.now()));
      worker.addEventListener("statechange", check);
      check();
    });
  }
}

// "An update to the portal is ready — Reload" banner. The PWA downloads
// updates in the background but keeps running the old copy until a reload;
// before this banner, pilots (especially on Safari) had to be told to reload
// twice.
// Tapping Reload activates the waiting service worker, waits until it has
// taken over, and refreshes once.
//
// HARD RULE (2026-07-24, Jill + Paul both bricked): NEVER activate a waiting
// service worker unless the internet is REALLY reachable. Activating swaps to
// the new build AND (cleanupOutdatedCaches) deletes the old precache — if the
// new build's hashed chunks aren't reachable, the app boots into a dead shell
// with no way back. navigator.onLine is NOT enough: on PPU wifi (AIS nav link,
// no internet) it reads TRUE. So Reload does a real-internet probe first and
// only activates on a confirmed round-trip. Offline/PPU, the old copy keeps
// running untouched and the prompt returns once the connection is truly back.

// True while a Reload tap is waiting for the new worker. The plugin's own
// "new worker took control" reload would otherwise fire first and cut the
// wait short (Codex on #582).
let tapInFlight = false;

export default function UpdateBanner() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Without this the plugin reloads the moment the new worker takes control.
    // During a tap, doReload reloads once the worker is fully activated; any
    // other time (another tab updated us) keep the plugin's reload.
    onNeedReload() {
      if (!tapInFlight) window.location.reload();
    },
    // re-check for a new version when the app comes back to the foreground
    // (pilots leave the tab open for days)
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => registration.update().catch(() => {});
      setInterval(check, 60 * 60 * 1000); // hourly while open
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
      });
    },
  });

  const [online, setOnline] = useState(navigator.onLine);
  const [checking, setChecking] = useState(false);
  const [noNet, setNoNet] = useState(false);
  useEffect(() => {
    const on = () => { setOnline(true); setNoNet(false); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (needRefresh) reportIfBannerCameBack();
  }, [needRefresh]);

  // Hidden while the browser is sure it's offline. When onLine is true we still
  // show it (can't trust that flag on PPU/ship wifi) but the real gate is the
  // probe on tap below.
  if (!needRefresh || !online) return null;

  const doReload = async () => {
    // The destructive step is activating the new SW; guard it behind a REAL
    // internet check, not navigator.onLine (true on PPU wifi with no internet).
    setChecking(true);
    setNoNet(false);
    const reachable = await probeInternet(4000);
    if (!reachable) {
      setChecking(false);
      setNoNet(true);   // keep the old copy running; tell the pilot why
      return;
    }
    // Tell the waiting worker to take over, WAIT until it has (up to 10 s),
    // then reload once. The old code reloaded on a fixed 1.5 s timer, which on
    // a slow device can come before the new worker is ready. The plugin's own
    // reload on "took control" is held off while tapInFlight (onNeedReload
    // above), and on a page no worker controls (after a hard reload, common
    // on iOS) it never fires at all, so the reload below finishes every tap.
    rememberReloadTap();
    tapInFlight = true;
    const reg = await navigator.serviceWorker?.getRegistration().catch(() => null);
    await updateServiceWorker(true).catch(() => {});
    // untilActivated also sends SKIP_WAITING straight to the worker it waits
    // on, in case the plugin's own copy of the registration has not seen it
    await untilActivated(reg, 10000);
    window.location.reload();
    // An open slip, expense or dispatch edit asks "Leave this page?" first.
    // If the pilot chooses Stay, the page is still here: give the button back
    // and let the plugin's reload work again (Codex on #582).
    setTimeout(() => {
      tapInFlight = false;
      setChecking(false);
    }, 3000);
  };

  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 2000,
      background: theme.colors.primary, color: "white",
      display: "flex", justifyContent: "center", alignItems: "center",
      gap: 14, padding: "10px 16px", flexWrap: "wrap",
      boxShadow: "0 -2px 8px rgba(0,0,0,0.25)",
    }}>
      <span style={{ fontSize: "0.92rem" }}>
        {noNet
          ? "No internet right now. The portal will update when you're back online. You can keep working."
          : "An update to the portal is ready."}
      </span>
      <button
        onClick={doReload}
        disabled={checking}
        style={{ background: "white", color: theme.colors.primary, border: "none", borderRadius: 6, padding: "7px 18px", fontWeight: 700, cursor: checking ? "default" : "pointer", opacity: checking ? 0.7 : 1 }}
      >
        {checking ? "Updating…" : noNet ? "Try Reload again" : "Reload"}
      </button>
      <button
        onClick={() => setNeedRefresh(false)}
        style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.6)", borderRadius: 6, padding: "6px 14px", fontWeight: 600, cursor: "pointer" }}
      >
        Later
      </button>
    </div>
  );
}
